import React, { useEffect } from "react";
import { CheckCircle, Shield, Zap, Target, ArrowRight, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const stats = [
  { value: "24/7", label: "Monitoring", color: "text-[#005a9c]" },
  { value: "99.9%", label: "Uptime", color: "text-[#005a9c]" },
  { value: "10x", label: "Response", color: "text-[#005a9c]" }
];

const RiskFort = () => {
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start('visible');
  }, [controls]);

  return (
    <div className="text-[#2c2c2c] overflow-x-hidden">

      {/* Hero Section */}
      <motion.section
        className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 overflow-hidden bg-white/40 border-b border-[#e5eef6]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <motion.div
              className="lg:w-1/2 space-y-6"
              variants={fadeInUp}
            >
              <motion.div
                className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-1.5 rounded-full border border-[#005a9c]/20"
                variants={fadeInUp}
              >
                <Shield className="w-4 h-4 text-[#005a9c]" />
                <span className="text-[#005a9c] text-sm font-medium tracking-wider uppercase">RiskFort</span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-slate-900"
                variants={fadeInUp}
              >
                AI-Powered <span className="text-[#005a9c]">Risk Management</span>
              </motion.h1>

              <motion.p
                className="text-lg text-slate-600 leading-relaxed max-w-xl"
                variants={fadeInUp}
              >
                Radar helps your teams swiftly identify and handle security threats,
                offering complete visibility across your entire AI environment with
                enterprise-grade protection and governance.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 pt-2"
                variants={fadeInUp}
              >
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="group relative bg-[#005a9c] text-white font-bold px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-[#003d6b] flex items-center justify-center shadow-lg"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>BOOK A DEMO</span>
                    <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>

                <motion.button
                  onClick={() => navigate('/aboutus')}
                  className="flex items-center justify-center px-6 py-3.5 text-[#005a9c] font-medium rounded-lg border-2 border-[#005a9c] hover:bg-[#005a9c]/5 transition-colors"
                  whileHover={{ y: -2 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>LEARN MORE</span>
                    <Zap className="w-4 h-4 ml-2" />
                  </span>
                </motion.button>
              </motion.div>

              {/* Trust Badges */}
              <motion.div
                className="flex flex-wrap items-center gap-6 opacity-80"
                variants={fadeInUp}
              >
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-slate-600">Enterprise Security</span>
                </div>
                <div className="h-6 w-px bg-slate-300" />
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-slate-600">AI-Powered Detection</span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.div
                className="relative z-10 bg-white/80 backdrop-blur-lg rounded-2xl p-8 border border-[#e5eef6] shadow-2xl"
                whileHover={{ y: -5 }}
              >
                <div className="relative">
                  <h3 className="text-xl font-bold mb-6 text-slate-900 flex items-center">
                    <Shield className="w-6 h-6 mr-2 text-[#005a9c]" />
                    Risk Assessment
                  </h3>
                  <div className="space-y-6">
                    {[
                      { label: "Threat Detection", value: 94, color: "bg-[#005a9c]" },
                      { label: "Vulnerability Coverage", value: 89, color: "bg-blue-500" },
                      { label: "Compliance Score", value: 97, color: "bg-green-500" }
                    ].map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm text-slate-600 mb-1.5">
                          <span>{item.label}</span>
                          <span className="font-semibold">{item.value}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full ${item.color} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: `${item.value}%` }}
                            transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      {stats.map((stat, index) => (
                        <div key={index}>
                          <div className={`text-2xl font-bold ${stat.color}`}>
                            {stat.value}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* AI Risk Assessment Section */}
      <motion.section
        className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#f0f7ff] border-b border-[#e5eef6] overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div
              className="lg:w-1/2 relative"
              variants={fadeInUp}
            >
              <div className="relative bg-white p-2 rounded-2xl border border-[#e5eef6] shadow-xl">
                <motion.img
                  src="https://cdn-icons-png.flaticon.com/512/3062/3062632.png"
                  alt="AI Risk Assessment"
                  className="w-full h-auto object-contain opacity-80 p-8"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                />
              </div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 space-y-6"
              variants={fadeInUp}
            >
              <div className="inline-block px-4 py-1.5 text-sm font-medium text-yellow-600 bg-yellow-400/10 rounded-full border border-yellow-400/20 uppercase tracking-wider">
                AI Risk Evaluation
              </div>

              <h2 className="text-4xl font-bold text-slate-900 leading-tight">
                Comprehensive Evaluation of Your AI Ecosystem
              </h2>

              <p className="text-lg text-slate-600 leading-relaxed">
                We dive deep into your ML Operations lifecycle, carefully examining
                the core AI models that drive your business. Our thorough review
                uncovers the risks tied to your AI investments.
              </p>

              <div className="space-y-4 pt-4">
                {[
                  {
                    title: "Model Analysis",
                    desc: "In-depth examination of AI/ML models for vulnerabilities and biases.",
                    icon: <Shield className="w-5 h-5 text-[#005a9c]" />,
                  },
                  {
                    title: "Framework Alignment",
                    desc: "Assessment aligned with NIST, MITRE ATLAS, and OWASP standards.",
                    icon: <Target className="w-5 h-5 text-[#005a9c]" />,
                  },
                  {
                    title: "Actionable Insights",
                    desc: "Practical recommendations to mitigate risks and enhance model security.",
                    icon: <CheckCircle className="w-5 h-5 text-[#005a9c]" />,
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="rf-card p-5 flex items-start space-x-4 hover:border-[#005a9c]/20 group transition-all bg-white"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 group-hover:bg-[#005a9c]/10 transition-colors">
                        {item.icon}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 group-hover:text-[#005a9c] transition-colors">{item.title}</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Maximize Productivity Section */}
      <motion.section
        className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-transparent border-b border-[#e5eef6] overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16 space-y-4"
            variants={fadeInUp}
          >
            <div className="inline-block px-4 py-1.5 text-sm font-medium text-[#005a9c] bg-[#005a9c]/10 rounded-full border border-[#005a9c]/20 uppercase tracking-wider">
              Risk Management
            </div>

            <h2 className="text-4xl font-bold text-slate-900">
              Maximize Productivity, <span className="text-[#005a9c]">Minimize Risk</span>
            </h2>

            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              The adoption of AI presents unique challenges for organizations to 
              maintain application security and compliance at enterprise scale.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Threat Intelligence",
                description: "Real-time monitoring and analysis of emerging AI threats and vulnerabilities.",
                icon: <Shield className="w-8 h-8 text-[#005a9c]" />,
              },
              {
                title: "Compliance Automation",
                description: "Automated compliance checks against industry standards and global regulations.",
                icon: <CheckCircle className="w-8 h-8 text-[#005a9c]" />,
              },
              {
                title: "Risk Mitigation",
                description: "Proactive strategies to reduce AI-related risks and technical vulnerabilities.",
                icon: <Zap className="w-8 h-8 text-[#005a9c]" />,
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="rf-card p-10 text-center bg-white"
                whileHover={{ y: -5 }}
              >
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">{feature.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Solution Section */}
      <motion.section
        className="bg-[#f0f7ff] py-24 relative overflow-hidden border-b border-[#e5eef6]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
          <motion.div
            className="text-center max-w-4xl mx-auto mb-16 space-y-4"
            variants={fadeInUp}
          >
            <div className="inline-block px-4 py-1.5 text-sm font-medium text-yellow-600 bg-yellow-400/10 rounded-full border border-yellow-400/20 uppercase tracking-wider">
              Our Solution
            </div>

            <h2 className="text-4xl font-bold text-slate-900">
              End-to-End AI Risk Management
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed">
              Receive a comprehensive analysis of your AI technology stack, including a
              threat model, security architecture review, and team awareness training.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Threat Analysis",
                description: "Understand how cybercriminals could exploit your specific AI applications and pipelines.",
                icon: <Shield className="w-8 h-8 text-[#005a9c]" />,
              },
              {
                title: "Resilience Testing",
                description: "Test AI application resilience through scenario-based adversarial attack simulations.",
                icon: <Zap className="w-8 h-8 text-[#005a9c]" />,
              },
              {
                title: "Integrity Audit",
                description: "Audit application integrity using robustness-focused stress testing methodologies.",
                icon: <CheckCircle className="w-8 h-8 text-[#005a9c]" />,
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="rf-card p-8 group transition-all bg-white"
                whileHover={{ y: -8 }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-slate-50 border border-slate-100 group-hover:bg-[#005a9c]/10 transition-colors">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">{item.title}</h3>
                <p className="text-slate-600 mb-6 text-sm leading-relaxed">{item.description}</p>
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="group flex items-center text-sm font-bold text-[#005a9c] hover:text-[#003d6b] transition-colors"
                >
                  <span>Learn more</span>
                  <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            className="mt-24 bg-[#005a9c] rounded-2xl p-10 text-center relative overflow-hidden shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative z-10 max-w-3xl mx-auto space-y-6">
              <h3 className="text-3xl font-bold text-white">Ready to Secure Your AI Infrastructure?</h3>
              <p className="text-blue-100 text-lg leading-relaxed">
                Join leading enterprises that trust RakFort for their AI governance and security needs.
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="bg-white text-[#005a9c] font-bold px-10 py-4 rounded-lg shadow-xl hover:bg-slate-50 transition-all flex items-center justify-center"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Started or Contact Sales
                  <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default RiskFort;
